import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import type {
  CreateExpenseDto,
  UpdateExpenseDto,
  MemberBalanceDto,
  SettlementDto,
  BalanceSummaryDto,
} from "@tabipla/shared";
import { ApiError, type ExpenseResponseDto } from "@tabipla/shared";
import { sessionKeys } from "./useSessions";

// Query keys
const expenseKeys = {
  all: ["expenses"] as const,
  session: (groupId: string, sessionId: string) =>
    [...expenseKeys.all, groupId, sessionId] as const,
  detail: (groupId: string, sessionId: string, expenseId: string) =>
    [...expenseKeys.session(groupId, sessionId), expenseId] as const,
  balances: (groupId: string, sessionId: string) =>
    [...expenseKeys.session(groupId, sessionId), "balances"] as const,
  settlements: (groupId: string, sessionId: string) =>
    [...expenseKeys.session(groupId, sessionId), "settlements"] as const,
};

// Fetch expenses for a session
export function useExpenses(groupId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.session(groupId ?? "", sessionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<ExpenseResponseDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch expenses");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Fetch single expense
export function useExpense(
  groupId: string | undefined,
  sessionId: string | undefined,
  expenseId: string | undefined
) {
  return useQuery({
    queryKey: expenseKeys.detail(groupId ?? "", sessionId ?? "", expenseId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<ExpenseResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/${expenseId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch expense");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId && !!expenseId,
  });
}

// Fetch balances for a session
export function useBalances(groupId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.balances(groupId ?? "", sessionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<MemberBalanceDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/balances`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch balances");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Fetch settlements for a session
export function useSettlements(groupId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.settlements(groupId ?? "", sessionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<SettlementDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/settlements`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch settlements");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Create expense mutation
export function useCreateExpense(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expense: CreateExpenseDto) => {
      const response = await apiClient.post<ExpenseResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses`,
        expense
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create expense");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.session(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.balances(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.settlements(groupId, sessionId) });
      // Also invalidate session to update totalExpenses/expenseCount
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

// Update expense mutation
export function useUpdateExpense(groupId: string, sessionId: string, expenseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expense: UpdateExpenseDto) => {
      const response = await apiClient.put<ExpenseResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/${expenseId}`,
        expense
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update expense");
      }
      return response.data;
    },
    onMutate: async (expense) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: expenseKeys.session(groupId, sessionId) });

      // Snapshot previous value
      const previousExpenses = queryClient.getQueryData<ExpenseResponseDto[]>(
        expenseKeys.session(groupId, sessionId)
      );

      // Optimistically update only the basic fields (not shares which has different structure)
      if (previousExpenses) {
        queryClient.setQueryData<ExpenseResponseDto[]>(
          expenseKeys.session(groupId, sessionId),
          previousExpenses.map((e) =>
            e.id === expenseId
              ? {
                  ...e,
                  amount: expense.amount ?? e.amount,
                  description: expense.description ?? e.description,
                  category: expense.category ?? e.category,
                  date: expense.date ?? e.date,
                  updatedAt: new Date().toISOString(),
                }
              : e
          )
        );
      }

      return { previousExpenses };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(expenseKeys.session(groupId, sessionId), context.previousExpenses);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.session(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.balances(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.settlements(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

// Delete expense mutation
export function useDeleteExpense(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expenseId: string) => {
      const response = await apiClient.delete<null>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/${expenseId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete expense");
      }
      return expenseId;
    },
    onMutate: async (expenseId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: expenseKeys.session(groupId, sessionId) });

      // Snapshot previous value
      const previousExpenses = queryClient.getQueryData<ExpenseResponseDto[]>(
        expenseKeys.session(groupId, sessionId)
      );

      // Optimistically remove the expense
      if (previousExpenses) {
        queryClient.setQueryData<ExpenseResponseDto[]>(
          expenseKeys.session(groupId, sessionId),
          previousExpenses.filter((expense) => expense.id !== expenseId)
        );
      }

      return { previousExpenses };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(expenseKeys.session(groupId, sessionId), context.previousExpenses);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.session(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.balances(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.settlements(groupId, sessionId) });
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

// Fetch balance summary for the current user across all groups
export function useBalanceSummary() {
  return useQuery({
    queryKey: ["balanceSummary"] as const,
    queryFn: async () => {
      const response = await apiClient.get<BalanceSummaryDto>("/api/me/balance-summary");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch balance summary");
      }
      return response.data;
    },
  });
}

// Fetch balance for a specific group
export function useGroupBalance(groupId: string | undefined) {
  const { data: summary, isLoading, error } = useBalanceSummary();

  const groupBalance = summary?.groups.find((g) => g.groupId === groupId);

  return {
    data: groupBalance,
    isLoading,
    error,
  };
}
